/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Component,
  EventEmitter,
  Input,
  Output,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';

@Component({
  selector: 'sag-ui-textarea',
  template: `
    <div class="flex flex-col">
      <div class="flex flex-col-reverse w-full relative">
        <textarea
          sag-ui-input
          [id]="id"
          [disabled]="disabled"
          [required]="invalid || (required && touched)"
          [attr.data-invalid]="invalid"
          [placeholder]="floatLabel ? '' : placeholder"
          [value]="value"
          (change)="onChange($event)"
          (click)="click.emit($event)"
          (focus)="focused = true; focus.emit($event)"
          (blur)="onBlur($event)"
          (keydown)="keydown.emit($event)"
          (keyup)="keyup.emit($event)"
          (mouseenter)="mouseenter.emit($event)"
          (mouseleave)="mouseleave.emit($event)"
          [attr.aria-label]="ariaLabel"
          [attr.aria-labelledby]="ariaLabelledby || id"
          [attr.aria-describedby]="ariaDescribedby"
          [attr.data-float-label]="floatLabel"
        ></textarea>
        <label
          sag-ui-input-label
          [htmlFor]="id"
          [attr.data-float]="floatLabel ? focused || !!value : undefined"
          [attr.data-float-label]="floatLabel"
        >
          <span *ngIf="required">*</span>
          {{ label }}
          <span
            *ngIf="hint"
            class="text-[#999999] dark:text-[#666666] icon-circle-information pl-2"
            sag-ui-tooltip
            [tooltip]="hint"
          ></span>
        </label>
      </div>
      <div *ngIf="error && isArray(error)" class="flex flex-col gap-1">
        <span sag-ui-input-error *ngFor="let err of error">{{ err }}</span>
      </div>
      <span sag-ui-input-error *ngIf="error && !isArray(error)">{{
        error
      }}</span>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextareaComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TextareaComponent),
      multi: true,
    },
  ],
})
export class TextareaComponent implements ControlValueAccessor, Validator {
  @Input() id = this.randomId();
  @Input() disabled = false;
  @Input() required = false;
  @Input() invalid: boolean | null = null;
  @Input() error: string | string[] | null = null;
  @Input() label = '';
  @Input() value = '';
  @Input() placeholder = '';
  @Input() readOnly = false;
  @Input() hint?: string;
  @Input() floatLabel = false;

  // Accessibility inputs
  @Input() ariaLabel: string | null = null;
  @Input() ariaLabelledby: string | null = null;
  @Input() ariaDescribedby: string | null = null;

  // Outputs for events
  @Output() change = new EventEmitter<Event>();
  @Output() input = new EventEmitter<Event>();
  @Output() click = new EventEmitter<Event>();
  @Output() focus = new EventEmitter<Event>();
  @Output() blur = new EventEmitter<Event>();
  @Output() keydown = new EventEmitter<KeyboardEvent>();
  @Output() keyup = new EventEmitter<KeyboardEvent>();
  @Output() mouseenter = new EventEmitter<MouseEvent>();
  @Output() mouseleave = new EventEmitter<MouseEvent>();

  touched = false;
  protected focused = false;
  onChangeEvent = (_: any) => {};
  onTouched = () => {};

  writeValue(obj: any): void {
    this.value = obj;
  }

  registerOnChange(fn: any): void {
    this.onChangeEvent = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onBlur(event: Event): void {
    event.stopPropagation();
    this.touched = true;
    this.focused = false;
    this.onTouched();
    this.blur.emit(event);
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onChange(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    const input = event.target as HTMLInputElement;
    this.value = input.value;
    this.onChangeEvent(this.value);
    this.change.emit(event);
  }

  validate(_: AbstractControl): ValidationErrors | null {
    return this.required && !this.value ? { required: true } : null;
  }

  isArray(value: string | string[]): value is string[] {
    return Array.isArray(value);
  }

  private randomId(): string {
    return Math.random().toString(36).substring(2);
  }
}
